import { Upload, X, ImageIcon, Video } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { MediaFile } from '../model/types';

interface CarouselMediaUploadProps {
  mediaFiles: MediaFile[];
  onAddMedia: (files: File[]) => void;
  onRemoveMedia: (id: string) => void;
  maxFiles?: number;
}

export function CarouselMediaUpload({
  mediaFiles,
  onAddMedia,
  onRemoveMedia,
  maxFiles = 10,
}: CarouselMediaUploadProps) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onAddMedia(files);
    }
    // Сбрасываем input чтобы можно было выбрать те же файлы снова
    e.target.value = '';
  };

  const handleUploadClick = () => {
    document.getElementById('carousel-media-upload')?.click();
  };

  const canAddMore = mediaFiles.length < maxFiles;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm">
          Загрузить медиа ({mediaFiles.length}/{maxFiles})
        </p>
      </div>

      <input
        id="carousel-media-upload"
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* Загруженные медиа */}
        {mediaFiles.map((media, index) => (
          <div
            key={media.id}
            className="relative aspect-square rounded-lg overflow-hidden border bg-muted group"
          >
            {media.type === 'image' ? (
              <img
                src={media.preview}
                alt={`Media ${index + 1}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                src={media.preview}
                className="w-full h-full object-cover"
                muted
              />
            )}
            
            {/* Номер */}
            <div className="absolute top-2 left-2 bg-black/70 text-white rounded px-2 py-1 text-xs flex items-center gap-1">
              {media.type === 'video' ? (
                <Video className="h-3 w-3" />
              ) : (
                <ImageIcon className="h-3 w-3" />
              )}
              {index + 1}
            </div>

            {/* Кнопка удаления */}
            <button
              onClick={() => onRemoveMedia(media.id)}
              className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}

        {/* Кнопка загрузки */}
        <div
          onClick={handleUploadClick}
          className="relative aspect-square rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ pointerEvents: canAddMore ? 'auto' : 'none', opacity: canAddMore ? 1 : 0.5 }}
        >
          <div className="text-center">
            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">
              Загрузить
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
